import { useState, useEffect } from "react";
import { MatchmakerLayout } from "@/components/matchmaker/MatchmakerLayout";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, TrendingUp, Users, CheckCircle, XCircle, Clock } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { useUserRole } from "@/hooks/useUserRole";

interface AnalyticsData {
  totalMatches: number;
  pendingMatches: number;
  acceptedMatches: number;
  rejectedMatches: number;
  bothAcceptedMatches: number;
  averageCompatibility: number;
  acceptanceRate: number;
  topCriteria: Array<{ criterion: string; count: number }>;
  recentActivity: Array<{ date: string; matches: number }>;
}

export default function MatchmakerAnalytics() {
  const { isAdmin } = useUserRole();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState<AnalyticsData>({
    totalMatches: 0,
    pendingMatches: 0,
    acceptedMatches: 0,
    rejectedMatches: 0,
    bothAcceptedMatches: 0,
    averageCompatibility: 0,
    acceptanceRate: 0,
    topCriteria: [],
    recentActivity: [],
  });

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      // Fetch all matches
      const { data: matches, error: matchesError } = await supabase
        .from("matches")
        .select("*");

      if (matchesError) throw matchesError;

      // Fetch match suggestions for compatibility data
      const { data: suggestions, error: suggestionsError } = await supabase
        .from("match_suggestions")
        .select("compatibility_score, compatibility_details");

      if (suggestionsError) throw suggestionsError;

      // Calculate metrics
      const totalMatches = matches?.length || 0;
      const pendingMatches = matches?.filter((m) => m.status === "pending").length || 0;
      const acceptedByOne = matches?.filter(
        (m) => m.status === "accepted_by_1" || m.status === "accepted_by_2"
      ).length || 0;
      const bothAcceptedMatches = matches?.filter((m) => m.status === "both_accepted").length || 0;
      const rejectedMatches = matches?.filter((m) => m.status === "rejected").length || 0;

      const acceptedMatches = acceptedByOne + bothAcceptedMatches;
      const acceptanceRate =
        totalMatches > 0 ? ((acceptedMatches / totalMatches) * 100).toFixed(1) : "0";

      // Calculate average compatibility from suggestions
      const avgCompatibility =
        suggestions && suggestions.length > 0
          ? (
              suggestions.reduce((sum, s) => sum + (s.compatibility_score || 0), 0) /
              suggestions.length
            ).toFixed(1)
          : "0";

      // Analyze top criteria from compatibility details
      const criteriaCount: Record<string, number> = {};
      suggestions?.forEach((suggestion) => {
        const details = suggestion.compatibility_details as any;
        if (details?.details && Array.isArray(details.details)) {
          details.details.forEach((detail: any) => {
            if (detail.match === true) {
              criteriaCount[detail.category] = (criteriaCount[detail.category] || 0) + 1;
            }
          });
        }
      });

      const topCriteria = Object.entries(criteriaCount)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 5)
        .map(([criterion, count]) => ({ criterion, count }));

      // Calculate recent activity (last 7 days)
      const now = new Date();
      const recentActivity = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date(now);
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split("T")[0];

        const matchesOnDay =
          matches?.filter((m) => {
            const createdDate = new Date(m.created_at).toISOString().split("T")[0];
            return createdDate === dateStr;
          }).length || 0;

        recentActivity.push({
          date: date.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
          matches: matchesOnDay,
        });
      }

      setAnalytics({
        totalMatches,
        pendingMatches,
        acceptedMatches,
        rejectedMatches,
        bothAcceptedMatches,
        averageCompatibility: parseFloat(avgCompatibility),
        acceptanceRate: parseFloat(acceptanceRate),
        topCriteria,
        recentActivity,
      });
    } catch (error) {
      console.error("analytics.fetch_failed");
    } finally {
      setLoading(false);
    }
  };

  const Layout = isAdmin ? AdminLayout : MatchmakerLayout;
  const layoutTitle = isAdmin ? "Analytics (System-wide)" : "Analytics";

  if (loading) {
    return (
      <Layout title={layoutTitle}>
        <div className="flex items-center justify-center h-[calc(100vh-200px)]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  const maxActivityMatches = Math.max(...analytics.recentActivity.map((a) => a.matches), 1);

  return (
    <Layout title={layoutTitle}>
      <div className="container mx-auto p-6 space-y-6">
        <div>
          <h1 className="text-3xl font-bold mb-2">{isAdmin ? "System Analytics" : "Matchmaker Analytics"}</h1>
          <p className="text-muted-foreground">{isAdmin ? "System-wide performance metrics" : "Performance metrics and insights"}</p>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Matches</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics.totalMatches}</div>
              <p className="text-xs text-muted-foreground">All time</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Acceptance Rate</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics.acceptanceRate}%</div>
              <p className="text-xs text-muted-foreground">
                {analytics.acceptedMatches} of {analytics.totalMatches} accepted
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Avg Compatibility</CardTitle>
              <CheckCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics.averageCompatibility}%</div>
              <p className="text-xs text-muted-foreground">From match suggestions</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending Matches</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics.pendingMatches}</div>
              <p className="text-xs text-muted-foreground">Awaiting responses</p>
            </CardContent>
          </Card>
        </div>

        {/* Match Status Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Match Status Distribution</CardTitle>
              <CardDescription>Current status of all created matches</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-sm font-medium">Both Accepted</span>
                  </div>
                  <span className="text-sm font-bold">{analytics.bothAcceptedMatches}</span>
                </div>
                <Progress
                  value={
                    analytics.totalMatches > 0
                      ? (analytics.bothAcceptedMatches / analytics.totalMatches) * 100
                      : 0
                  }
                  className="h-2"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-yellow-500" />
                    <span className="text-sm font-medium">Pending</span>
                  </div>
                  <span className="text-sm font-bold">{analytics.pendingMatches}</span>
                </div>
                <Progress
                  value={
                    analytics.totalMatches > 0
                      ? (analytics.pendingMatches / analytics.totalMatches) * 100
                      : 0
                  }
                  className="h-2"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-blue-500" />
                    <span className="text-sm font-medium">Partially Accepted</span>
                  </div>
                  <span className="text-sm font-bold">
                    {analytics.acceptedMatches - analytics.bothAcceptedMatches}
                  </span>
                </div>
                <Progress
                  value={
                    analytics.totalMatches > 0
                      ? ((analytics.acceptedMatches - analytics.bothAcceptedMatches) /
                          analytics.totalMatches) *
                        100
                      : 0
                  }
                  className="h-2"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <XCircle className="h-4 w-4 text-red-500" />
                    <span className="text-sm font-medium">Rejected</span>
                  </div>
                  <span className="text-sm font-bold">{analytics.rejectedMatches}</span>
                </div>
                <Progress
                  value={
                    analytics.totalMatches > 0
                      ? (analytics.rejectedMatches / analytics.totalMatches) * 100
                      : 0
                  }
                  className="h-2"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Top Matching Criteria</CardTitle>
              <CardDescription>Most successful compatibility factors</CardDescription>
            </CardHeader>
            <CardContent>
              {analytics.topCriteria.length > 0 ? (
                <div className="space-y-3">
                  {analytics.topCriteria.map((item, index) => (
                    <div key={item.criterion} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          #{index + 1}
                        </Badge>
                        <span className="text-sm font-medium capitalize">
                          {item.criterion.replace(/_/g, " ")}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">{item.count} matches</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No criteria data available yet. Create more matches to see insights.
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
            <CardDescription>Matches created in the last 7 days</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {analytics.recentActivity.map((activity) => (
                <div key={activity.date} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{activity.date}</span>
                    <span className="text-sm font-bold">{activity.matches} matches</span>
                  </div>
                  <div className="relative h-4 bg-muted rounded-full overflow-hidden">
                    <div
                      className="absolute left-0 top-0 h-full bg-primary rounded-full transition-all"
                      style={{
                        width: `${(activity.matches / maxActivityMatches) * 100}%`,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Success Rate Card */}
        <Card>
          <CardHeader>
            <CardTitle>Success Insights</CardTitle>
            <CardDescription>Key performance indicators</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Full Acceptance Rate</p>
                <p className="text-3xl font-bold">
                  {analytics.totalMatches > 0
                    ? ((analytics.bothAcceptedMatches / analytics.totalMatches) * 100).toFixed(1)
                    : 0}
                  %
                </p>
                <p className="text-xs text-muted-foreground">
                  {analytics.bothAcceptedMatches} of {analytics.totalMatches} fully accepted
                </p>
              </div>

              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Rejection Rate</p>
                <p className="text-3xl font-bold">
                  {analytics.totalMatches > 0
                    ? ((analytics.rejectedMatches / analytics.totalMatches) * 100).toFixed(1)
                    : 0}
                  %
                </p>
                <p className="text-xs text-muted-foreground">
                  {analytics.rejectedMatches} of {analytics.totalMatches} rejected
                </p>
              </div>

              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Response Rate</p>
                <p className="text-3xl font-bold">
                  {analytics.totalMatches > 0
                    ? (
                        ((analytics.totalMatches - analytics.pendingMatches) /
                          analytics.totalMatches) *
                        100
                      ).toFixed(1)
                    : 0}
                  %
                </p>
                <p className="text-xs text-muted-foreground">
                  {analytics.totalMatches - analytics.pendingMatches} of {analytics.totalMatches}{" "}
                  responded
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
